import React, { useState } from 'react';
import { Search, MapPin, Calendar, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCategories } from '../hooks/useCategories';
import { useCities } from '../hooks/useCities';

interface SearchBarProps {
  onSearch: (filters: { city: string; category: string; date: string }) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({ onSearch }) => {
  const [city, setCity] = useState('');
  const [category, setCategory] = useState('');
  const [date, setDate] = useState('');
  const { t } = useTranslation();
  const { categories, loading: loadingCategories } = useCategories();
  const { cities, loading: loadingCities } = useCities();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch({ city, category, date });
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-8">
      <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
            {t('search.city')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MapPin className="h-5 w-5 text-gray-400" />
            </div>
            <select
              id="city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">{t('search.allCities')}</option>
              {loadingCities ? (
                <option value="" disabled>Loading cities...</option>
              ) : (
                cities.map((city) => (
                  <option key={city.id} value={city.name}>{city.name}</option>
                ))
              )}
            </select>
          </div>
        </div>
        
        <div className="flex-1">
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
            {t('search.eventType')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Tag className="h-5 w-5 text-gray-400" />
            </div>
            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">{t('search.allCategories')}</option>
              {loadingCategories ? (
                <option value="" disabled>Loading categories...</option>
              ) : (
                categories.map((cat) => (
                  <option key={cat.id} value={cat.slug} className="capitalize">
                    {cat.name}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>
        
        <div className="flex-1">
          <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
            {t('search.date')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Calendar className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="date"
              id="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>
        
        <div className="flex items-end">
          <button
            type="submit"
            className="w-full md:w-auto bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-md flex items-center justify-center transition-colors duration-300"
          >
            <Search className="h-5 w-5 mr-2" />
            {t('search.search')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SearchBar;